import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { ArrowRight, Star, ShieldCheck, Sparkles } from "lucide-react";
import TrendingStrip from "@/components/TrendingStrip";
import WeekendPromoStrip from "@/components/WeekendPromoStrip";

const HERO_IMG = "https://images.unsplash.com/photo-1597221336986-7a948756cd3a?w=1600&q=80";
const IMG2 = "https://images.unsplash.com/photo-1582533568805-78a15dcb01b5?w=800&q=80";
const IMG3 = "https://images.unsplash.com/photo-1761138785581-194503520539?w=800&q=80";
const IMG4 = "https://images.unsplash.com/photo-1760926455212-d42b180ad602?w=800&q=80";

export default function Landing() {
  const { t } = useApp();
  const nav = useNavigate();
  return (
    <div className="relative">
      <section className="relative overflow-hidden pt-10 pb-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 fade-up">
            <div className="overline text-[#005C7A] mb-6">{t("hero.badge")}</div>
            <h1 className="font-display text-5xl lg:text-6xl leading-[1.05] font-bold text-[#1A202C] mb-6">
              {t("hero.titleMain")}<br />
              <span className="text-[#005C7A]">{t("hero.titleAccent")}</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-lg mb-8 leading-relaxed">{t("hero.subtitle")}</p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => nav("/search")} className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta-search">
                {t("hero.cta")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-6 mt-10 text-sm text-muted-foreground flex-wrap">
              <div className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#005C7A]" /> {t("hero.trust1")}</div>
              <div className="flex items-center gap-1.5"><Star className="w-4 h-4 fill-[#E27B58] text-[#E27B58]" /> {t("hero.trust2")}</div>
              <div className="flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-[#E27B58]" /> {t("hero.trust3")}</div>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[520px]">
              <div className="col-span-4 row-span-4 rounded-3xl overflow-hidden shadow-xl">
                <img src={HERO_IMG} alt="Luxury pool" className="w-full h-full object-cover" />
              </div>
              <div className="col-span-2 row-span-3 rounded-3xl overflow-hidden shadow-lg mt-6">
                <img src={IMG3} alt="Pool bar" className="w-full h-full object-cover" />
              </div>
              <div className="col-span-2 row-span-3 rounded-3xl overflow-hidden shadow-lg">
                <img src={IMG4} alt="Sunset pool" className="w-full h-full object-cover" />
              </div>
              <div className="col-span-4 row-span-2 rounded-3xl overflow-hidden shadow-lg -mt-3">
                <img src={IMG2} alt="Pool detail" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-2xl px-5 py-4 flex items-center gap-3 border border-border">
              <div className="w-10 h-10 rounded-full bg-[#E27B58]/20 flex items-center justify-center">
                <Star className="w-5 h-5 fill-[#E27B58] text-[#E27B58]" />
              </div>
              <div>
                <div className="font-display text-xl font-bold">4.9 / 5</div>
                <div className="text-xs text-muted-foreground">{t("hero.ratingLabel")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <TrendingStrip />
      <WeekendPromoStrip />

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="overline text-[#E27B58] mb-3">{t("how.badge")}</div>
        <h2 className="font-display text-4xl font-bold mb-12 max-w-2xl">{t("how.title")}</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { n: "01", ti: t("how.s1t"), d: t("how.s1d") },
            { n: "02", ti: t("how.s2t"), d: t("how.s2d") },
            { n: "03", ti: t("how.s3t"), d: t("how.s3d") },
          ].map((s) => (
            <div key={s.n} className="relative p-8 bg-white border border-border rounded-2xl">
              <div className="font-display text-6xl font-bold text-[#E27B58]/25 mb-4">{s.n}</div>
              <h3 className="font-display text-2xl font-semibold mb-2">{s.ti}</h3>
              <p className="text-muted-foreground leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="relative rounded-3xl overflow-hidden bg-[#005C7A] text-white p-12 lg:p-20 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="overline text-[#E27B58] mb-4">{t("ownerCta.badge")}</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold leading-tight mb-4">{t("ownerCta.title")}</h2>
            <p className="text-white/80 mb-6 leading-relaxed">{t("ownerCta.subtitle")}</p>
            <Link to="/register/hotel" className="inline-flex items-center gap-2 bg-[#E27B58] hover:bg-[#C56A4A] transition px-6 py-3 rounded-full font-semibold" data-testid="cta-become-owner">
              {t("hero.ctaOwner")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="hidden md:block relative h-64 rounded-2xl overflow-hidden">
            <img src={IMG3} alt="Pool bar" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
}
